'use client';

import { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { fetchDocumentByNumber } from '@/redux/docsVerification/thunk';
import { clearDocumentDetails } from '@/redux/docsVerification/slice';
import { getProfessionalErrorMessage } from '@/utils/errorHandling';

import EVerifyDocumentSearchForm from './components/EVerifyDocumentSearchForm';
import VerificationLoadingCard from './components/VerificationLoadingCard';
import ErrorCard from './components/ErrorCard';
import DocumentVerificationCard from '../../documents/verify/components/DocumentVerificationCard';

interface ContactDetail {
  id: string;
  label: string;
  value: ReactNode;
}

interface Props {
  docNumber?: string;
  orgName: string;
  contactDetails: ContactDetail[];
}

type FieldKey = 'documentNo' | 'verificationReason';

interface InlineSearchBarProps {
  value: string;
  loading: boolean;
  buttonLabel?: string;
  onChange: (val: string) => void;
  onSearch: () => void;
  onReset: () => void;
}

function InlineSearchBar({
  value,
  loading,
  buttonLabel = 'Search',
  onChange,
  onSearch,
  onReset,
}: InlineSearchBarProps) {
  const canSearch = !!value.trim() && !loading;

  return (
    <div
      style={{
        marginTop: 20,
        marginBottom: 20,
        border: '1px solid var(--color-primary)',
        background: '#ffffff',
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'stretch',
        }}
      >
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSearch) onSearch();
          }}
          placeholder="Enter document number"
          style={{
            flex: '1 1 260px',
            minWidth: 220,
            padding: '12px 14px',
            fontSize: 14,
            border: '1px solid var(--color-primary)',
            outline: 'none',
            boxSizing: 'border-box',
            background: '#ffffff',
            color: '#1a1a1a',
          }}
        />
        <button
          onClick={onSearch}
          disabled={!canSearch}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '12px 20px',
            minWidth: 140,
            fontSize: 14,
            fontWeight: 600,
            color: '#ffffff',
            border: '1px solid var(--color-primary)',
            background: canSearch ? 'var(--color-primary)' : '#94a3b8',
            borderColor: canSearch ? 'var(--color-primary)' : '#94a3b8',
            cursor: canSearch ? 'pointer' : 'not-allowed',
          }}
        >
          {loading ? 'Searching...' : buttonLabel}
        </button>
        <button
          onClick={onReset}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '12px 20px',
            minWidth: 140,
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--color-primary)',
            border: '1px solid var(--color-primary)',
            background: '#ffffff',
            cursor: 'pointer',
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

function PageWrap({
  children,
  fullScreen = false,
}: {
  children: React.ReactNode;
  fullScreen?: boolean;
}) {
  return (
    <div
      className={`w-full max-w-5xl mx-auto px-4 sm:px-6 ${
        fullScreen ? 'py-0' : 'py-8 sm:py-10'
      }`}
    >
      {children}
    </div>
  );
}

export default function EVerifyDocumentsClient({
  docNumber,
  orgName,
  contactDetails,
}: Props) {
  const dispatch = useAppDispatch();
  const { loading, document, error } = useAppSelector(
    (state) => state.docsVerification,
  );

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const initialDocNo = searchParams.get('doc_no') || docNumber || '';

  const [values, setValues] = useState<Partial<Record<FieldKey, string>>>({
    documentNo: initialDocNo,
  });
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [submitted, setSubmitted] = useState(!!initialDocNo);

  const hasAutoFetchedRef = useRef(false);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    return () => {
      abortRef.current = true;
    };
  }, []);

  const isReady = !!(
    values.documentNo &&
    values.verificationReason &&
    captchaChecked
  );

  const handleInput = (key: FieldKey, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const updateUrl = useCallback(
    (docNo?: string) => {
      const params = new URLSearchParams(searchParams.toString());
      if (docNo?.trim()) {
        params.set('doc_no', docNo.trim());
      } else {
        params.delete('doc_no');
      }
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, {
        scroll: false,
      });
    },
    [searchParams, router, pathname],
  );

  const runSearch = useCallback(
    async (docNo: string, syncUrl = true) => {
      const trimmed = docNo.trim();
      if (!trimmed) return;

      if (syncUrl) updateUrl(trimmed);

      dispatch(clearDocumentDetails());
      await dispatch(fetchDocumentByNumber(trimmed));

      if (!abortRef.current) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [dispatch, updateUrl],
  );

  const handleSubmit = async () => {
    if (!isReady || !values.documentNo) return;
    setSubmitted(true);
    await runSearch(values.documentNo, true);
  };

  const handleResearch = async () => {
    if (!values.documentNo?.trim() || loading) return;
    setSubmitted(true);
    await runSearch(values.documentNo, true);
  };

  const handleBack = () => {
    dispatch(clearDocumentDetails());
    setSubmitted(false);
    setValues({ documentNo: '' });
    setCaptchaChecked(false);
    updateUrl('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const paramDocNo = searchParams.get('doc_no')?.trim();

    if (!paramDocNo) {
      hasAutoFetchedRef.current = false;
      return;
    }

    setValues((prev) => ({ ...prev, documentNo: paramDocNo }));
    setSubmitted(true);

    if (hasAutoFetchedRef.current) return;
    hasAutoFetchedRef.current = true;

    void runSearch(paramDocNo, false);
  }, [searchParams, runSearch]);

  if (!submitted) {
    return (
      <PageWrap>
        <EVerifyDocumentSearchForm
          documentNo={values.documentNo ?? ''}
          verificationReason={values.verificationReason ?? ''}
          captchaChecked={captchaChecked}
          isLoading={loading}
          isReady={isReady}
          orgName={orgName}
          onDocumentNoChange={(val) => handleInput('documentNo', val)}
          onReasonChange={(val) => handleInput('verificationReason', val)}
          onCaptchaChange={setCaptchaChecked}
          onSubmit={handleSubmit}
        />
      </PageWrap>
    );
  }

  if (loading) {
    return (
      <PageWrap fullScreen>
        <VerificationLoadingCard />
      </PageWrap>
    );
  }

  const searchBar = (label: string) => (
    <InlineSearchBar
      value={values.documentNo ?? ''}
      loading={loading}
      buttonLabel={label}
      onChange={(val) => handleInput('documentNo', val)}
      onSearch={handleResearch}
      onReset={handleBack}
    />
  );

  if (error) {
    return (
      <PageWrap>
        {searchBar('Try Again')}
        <ErrorCard
          error={error}
          safeErrorMessage={getProfessionalErrorMessage(error)}
          documentId={values.documentNo}
          contactDetails={contactDetails}
        />
      </PageWrap>
    );
  }

  if (!document) {
    return (
      <PageWrap>
        {searchBar('Search Again')}
        <ErrorCard
          error="not_found"
          safeErrorMessage="No document matched the provided number."
          documentId={values.documentNo}
          contactDetails={contactDetails}
        />
      </PageWrap>
    );
  }

  const isExpired = document.expiry_date
    ? new Date(document.expiry_date) < new Date()
    : false;

  return (
    <PageWrap>
      {searchBar('Search')}
      <DocumentVerificationCard document={document} isExpired={isExpired} />
    </PageWrap>
  );
}
